import React from "react";
import {
  Stack,
  NumberInput,
  Select,
  Text,
  Tooltip,
  Group,
  Switch,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  SLOT_INTERVAL_OPTIONS,
  DEFAULT_SLOT_INTERVAL_MINS,
  SLOT_INTERVAL_TOOLTIP,
} from "../../utils/formConstants";

function AdvancedSettingsFields({ formData, onChange, errors }) {
  return (
    <Stack>
      <NumberInput
        label={
          <Group gap="xs">
            <span>Booking Delay (minutes)</span>
            <Tooltip label="Sets minimum buffer time between now and when a customer can book their next appointment">
              <IconInfoCircle
                size="1rem"
                style={{ cursor: "help" }}
                color="gray"
              />
            </Tooltip>
          </Group>
        }
        value={formData?.booking_delay_mins}
        onChange={(value) => onChange("booking_delay_mins", value)}
        error={errors?.booking_delay_mins}
        min={0}
        max={2880} // 48 hours
        step={5}
      />

      <Select
        label={
          <Group gap="xs">
            <span>Availability Slot Interval</span>
            <Tooltip label={SLOT_INTERVAL_TOOLTIP}>
              <IconInfoCircle
                size="1rem"
                style={{ cursor: "help" }}
                color="gray"
              />
            </Tooltip>
          </Group>
        }
        data={SLOT_INTERVAL_OPTIONS}
        value={String(
          formData?.availability_slot_interval_mins ??
            DEFAULT_SLOT_INTERVAL_MINS,
        )}
        onChange={(value) =>
          onChange(
            "availability_slot_interval_mins",
            value ? parseInt(value, 10) : DEFAULT_SLOT_INTERVAL_MINS,
          )
        }
        error={errors?.availability_slot_interval_mins}
        allowDeselect={false}
      />

      <NumberInput
        label={
          <Group gap="xs">
            <span>Customer Booking Horizon (days)</span>
            <Tooltip label="Sets how far in advance customers can book appointments">
              <IconInfoCircle
                size="1rem"
                style={{ cursor: "help" }}
                color="gray"
              />
            </Tooltip>
          </Group>
        }
        value={formData?.customer_booking_horizon_days}
        onChange={(value) =>
          onChange(
            "customer_booking_horizon_days",
            value === "" || value === undefined ? null : value,
          )
        }
        error={errors?.customer_booking_horizon_days}
        placeholder="Unlimited"
        clampBehavior="none"
        min={0}
        max={365}
        step={1}
      />

      {formData?.customer_booking_horizon_days === 0 && (
        <Text c="red" size="sm">
          Customers can only book same-day, save field as blank if you want
          unlimited days in advanced
        </Text>
      )}

      <Switch
        label={
          <Group gap="xs">
            <span>Appointments Require Staff Approval</span>
            <Tooltip label="Requires staff approval for new self-service bookings before blocking the time in your calendar">
              <IconInfoCircle
                size="1rem"
                style={{ cursor: "help" }}
                color="gray"
              />
            </Tooltip>
          </Group>
        }
        checked={formData?.appts_need_accepting}
        onChange={(event) =>
          onChange("appts_need_accepting", event.currentTarget.checked)
        }
        error={errors?.appts_need_accepting}
      />

      <Switch
        label={
          <Group gap="xs">
            <span>Request Customer Appointment Confirmation</span>
            <Tooltip label="Sends confirmation requests to customers a few days before their scheduled appointment">
              <IconInfoCircle
                size="1rem"
                style={{ cursor: "help" }}
                color="gray"
              />
            </Tooltip>
          </Group>
        }
        checked={formData?.appts_need_confirmation}
        onChange={(event) =>
          onChange("appts_need_confirmation", event.currentTarget.checked)
        }
        error={errors?.appts_need_confirmation}
      />

      <Switch
        label={
          <Group gap="xs">
            <span>Deduct Discounts from Employee Commissions</span>
            <Tooltip label="When enabled, employee commissions will be calculated after discounts are applied rather than on the full price">
              <IconInfoCircle
                size="1rem"
                style={{ cursor: "help" }}
                color="gray"
              />
            </Tooltip>
          </Group>
        }
        checked={formData?.enable_discount_deduction_on_commissions}
        onChange={(event) =>
          onChange(
            "enable_discount_deduction_on_commissions",
            event.currentTarget.checked,
          )
        }
        error={errors?.enable_discount_deduction_on_commissions}
      />

      {/* Card-on-File has migrated from salon-wide to per-service configuration */}
    </Stack>
  );
}

export default AdvancedSettingsFields;
